import api from 'api';
import {INTEGRATION_OPTIONS_LABELS} from 'constants/integration-options';
import {PAYMENT_GATEWAY_OPTIONS} from 'constants/select-options';
import useAuth from 'hooks/useAuth';
import {encode} from 'js-base64';
import {get} from 'lodash';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useLocation} from 'react-router-dom';
import {ListResponse, SelectOption} from 'types';
import {Integration, IntegrationType} from 'types/integration';

type FormValues = {
  gateway_type: SelectOption;
  secret_api_key: string;
  public_api_key: string;
  developer_id: string;
  version_number: string;
  stripe_secret_api_key: string;
  stripe_public_key: string;
};

type GatewayResponse = {
  company_id: number;
  company_name: string;
  id: number;
  gateway_type: string;
};
type Tab = 'payment_gateway' | 'other_integrations' | 'api_keys' | 'webhooks';
const TABS: {label: string; value: Tab}[] = [
  {
    label: 'Payment Gateway',
    value: 'payment_gateway',
  },
  {
    label: 'Other Integrations',
    value: 'other_integrations',
  },
  {
    label: 'API',
    value: 'api_keys',
  },
  {
    label: 'Webhooks',
    value: 'webhooks',
  },
];

async function getGateways() {
  const {data} = await api.get<ListResponse<GatewayResponse>>(
    `/payment/gateways/`,
    {
      params: {
        limit: 20,
        offset: 0,
      },
    }
  );

  return data;
}

async function getIntegrations() {
  const {data} = await api.get<ListResponse<Integration>>(
    '/integrations/integration-configs/',
    {
      params: {
        limit: 20,
        offset: 0,
      },
    }
  );

  return data?.results;
}

const deleteIntegrationMutation = async (id: string) => {
  return await api.delete(`/integrations/integration-configs/${id}/`);
};

export const useIntegrations = () => {
  const queryClient = useQueryClient();
  const {currentUser, refetch: revalidateUser} = useAuth();
  const {state} = useLocation();

  const [isModalOpen, setModalOpen] = useState(false);
  const [connectIntegrationModal, setConnectIntegrationModal] =
    useState<IntegrationType | null>(null);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [isDeleteIntegrationModalOpen, setIsDeleteIntegrationModalOpen] =
    useState(false);
  const [currentIntegration, setCurrentIntegration] = useState<Integration>();
  const [submitting, setSubmitting] = useState(false);
  const {data, isLoading, refetch} = useQuery(['users', '1'], getGateways);
  const [currentGateWay, setCurrentGateWay] = useState<GatewayResponse>();
  const [tab, setTab] = useState<Tab>(
    (sessionStorage.getItem('integration_tab') as Tab) || 'payment_gateway'
  );
  const {refetch: refetchUser} = useAuth();

  const {mutateAsync: deleteIntegration, isLoading: isDeleting} = useMutation({
    mutationFn: deleteIntegrationMutation,
    onSuccess: () => setConnectIntegrationModal(null),
    mutationKey: ['delete-integration'],
  });

  const {data: integrationsData, isLoading: integrationsLoading} = useQuery(
    ['integrations'],
    getIntegrations
  );

  const {
    register,

    handleSubmit,
    reset,
    control,
    watch,
    formState: {errors},
  } = useForm<FormValues>({
    defaultValues: {
      gateway_type: PAYMENT_GATEWAY_OPTIONS[0],
    },
  });

  useEffect(() => {
    if (get(state, 'show_modal')) {
      const modalOpen = !!get(state, 'show_modal');
      setModalOpen(modalOpen);
    }
  }, [state]);

  const handleTabChange = (tab: Tab) => {
    sessionStorage.setItem('integration_tab', tab);
    setTab(tab);
  };
  const handleOrdersClose = () => {
    setModalOpen(false);
  };
  const submithandler = (data: FormValues) => {
    setSubmitting(true);
    api
      .post('/payment/gateways/', {
        gateway_type: data.gateway_type.value,
        credentials:
          data.gateway_type.value === 'heartland'
            ? {
                HEARTLAND_SECRET_API_KEY: encode(data.secret_api_key),
                HEARTLAND_PUBLIC_API_KEY: encode(data.public_api_key),
                HEARTLAND_DEVELOPER_ID: encode(data.developer_id),
                HEARTLAND_VERSION_NUMBER: encode(data.version_number),
              }
            : {
                STRIPE_SECRET_API_KEY: encode(data.stripe_secret_api_key),
                STRIPE_PUBLIC_API_KEY: encode(data.stripe_public_key),
              },
      })
      .then(() => {
        refetch();
        revalidateUser(true);
        reset();
        setModalOpen(false);
      })
      .catch(() => {})
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleDeletePaymentGateway = async () => {
    try {
      await api.delete(`/payment/gateways/${currentGateWay?.id}/`);
      refetch();
      setIsDeleteVisible(false);
    } catch (error) {}
  };

  const handleDeleteIntegration = async () => {
    if (!currentIntegration) return;
    const integrationType =
      INTEGRATION_OPTIONS_LABELS[currentIntegration.integration_type];
    try {
      if (!currentIntegration) return;
      await deleteIntegration(currentIntegration?.id);
      queryClient.invalidateQueries(['integrations']);
      if (currentIntegration.integration_type === 'google_drive') {
        refetchUser(true);
      }
      toast.success(`${integrationType} integration deleted successfully`);
    } catch (error) {
      const message = get(
        error,
        'response.data.error_message',
        `Failed to delete ${integrationType} integration`
      );
      toast.error(message);
    } finally {
      setIsDeleteIntegrationModalOpen(false);
    }
  };

  const openDeleteModal = (integration?: Integration) => {
    if (!integration) return;
    setCurrentIntegration(integration);
    setIsDeleteIntegrationModalOpen(true);
  };
  const handleConnectIntegration = (integrationType: IntegrationType) => {
    setCurrentIntegration(undefined);
    setConnectIntegrationModal(integrationType);
  };

  const refetchIntegrations = () => {
    queryClient.invalidateQueries(['integrations']);
  };

  return {
    isModalOpen,
    setModalOpen,
    connectIntegrationModal,
    setConnectIntegrationModal,
    isDeleteVisible,
    setIsDeleteVisible,
    isDeleteIntegrationModalOpen,
    setIsDeleteIntegrationModalOpen,
    currentIntegration,
    submitting,
    currentGateWay,
    setCurrentGateWay,
    tab,
    TABS,
    handleTabChange,
    handleOrdersClose,
    submithandler,
    handleDeletePaymentGateway,
    handleDeleteIntegration,
    openDeleteModal,
    handleConnectIntegration,
    data,
    isLoading,
    integrationsData,
    integrationsLoading,
    isDeleting,
    currentUser,
    control,
    watch,
    errors,
    register,
    handleSubmit,
    refetchIntegrations,
  };
};
